import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, FormControl, InputLabel, MenuItem, Paper, Select, TextField, Tooltip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { MaterialReactTable } from 'material-react-table';
import { useNavigate, useSearchParams } from 'react-router-dom';
import intercomService from '../../services/intercom.service';
import styles from './Intercom.module.css';

const DEFAULT_PAGE_SIZE = 10;
const STATUS_OPTIONS = [
	{ value: 'all', label: 'All' },
	{ value: 'open', label: 'Open' },
	{ value: 'closed', label: 'Closed' },
];
const TICKET_STATE_OPTIONS = [
	{ value: '', label: 'All states' },
	{ value: 'submitted', label: 'Submitted' },
	{ value: 'in_progress', label: 'In progress' },
	{ value: 'waiting_on_customer', label: 'Waiting on customer' },
	{ value: 'resolved', label: 'Resolved' },
];

const formatTimestamp = (timestamp) => {
	if (!timestamp) {
		return '';
	}
	const date = new Date(timestamp * 1000);
	return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
};

const getTicketAccountId = (ticket) => {
	const attributes = ticket?.ticket_attributes;
	if (!attributes) {
		return '';
	}
	if (attributes.account_id) {
		return attributes.account_id;
	}
	const entry = Object.entries(attributes).find(([key]) => key.toLowerCase().includes('account id'));
	return entry ? entry[1] || '' : '';
};

const getAuthorEmail = (ticket) => {
	const parts = ticket?.ticket_parts?.ticket_parts;
	if (!Array.isArray(parts)) {
		return '';
	}
	const nonBotPart = parts.find((part) => part?.author?.email && part?.author?.type !== 'bot');
	return nonBotPart?.author?.email || '';
};

const getConversationId = (ticket) => {
	if (!ticket) {
		return null;
	}
	if (ticket.id) {
		return ticket.id;
	}
	if (ticket.conversation_id) {
		return ticket.conversation_id;
	}
	if (ticket.conversationId) {
		return ticket.conversationId;
	}
	const linkedConversation = ticket?.linked_objects?.data?.find((item) => item?.type === 'conversation');
	return linkedConversation?.id || null;
};

const getTicketDetailId = (ticket) => {
	if (!ticket) {
		return null;
	}
	return ticket.id || null;
};

const IntercomTickets = () => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [filters, setFilters] = useState(() => ({
		status: searchParams.get('status') || 'all',
		ticketState: searchParams.get('ticketState') || '',
		accountId: searchParams.get('accountId') || '',
		conversationId: searchParams.get('conversationId') || '',
		ticketId: searchParams.get('ticketId') || '',
	}));
	const [appliedFilters, setAppliedFilters] = useState(() => ({
		status: searchParams.get('status') || 'all',
		ticketState: searchParams.get('ticketState') || '',
		accountId: searchParams.get('accountId') || '',
		conversationId: searchParams.get('conversationId') || '',
		ticketId: searchParams.get('ticketId') || '',
	}));
	const [page, setPage] = useState(() => {
		const pageParam = Number(searchParams.get('page') || 1);
		return Number.isNaN(pageParam) || pageParam < 1 ? 1 : pageParam;
	});
	const [tickets, setTickets] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const startingAfterByPage = useRef({});

	useEffect(() => {
		document.title = 'Intercom Tickets - GSD Support Admin';
		return () => {
			document.title = 'GSD Support Admin';
		};
	}, []);

	const loadTickets = useCallback(async () => {
		setIsLoading(true);
		setErrorMessage('');
		try {
			const startingAfter = page > 1 ? startingAfterByPage.current[page] : '';
			const data = await intercomService.searchTickets({
				page,
				perPage: DEFAULT_PAGE_SIZE,
				open: appliedFilters.status === 'all' ? '' : appliedFilters.status === 'open' ? 'true' : 'false',
				state: appliedFilters.ticketState,
				accountId: appliedFilters.accountId,
				conversationId: appliedFilters.conversationId,
				ticketId: appliedFilters.ticketId,
				startingAfter,
			});
			const result = data?.result || {};
			setTickets(result.tickets || []);
			setTotalCount(result.total_count || 0);
			const nextStartingAfter = result?.pages?.next?.starting_after || result?.starting_after || '';
			if (nextStartingAfter) {
				startingAfterByPage.current[page + 1] = nextStartingAfter;
			} else {
				delete startingAfterByPage.current[page + 1];
			}
		} catch (error) {
			setErrorMessage(error?.message || 'Failed to load tickets.');
		} finally {
			setIsLoading(false);
		}
	}, [appliedFilters, page]);

	useEffect(() => {
		loadTickets();
	}, [loadTickets]);

	const handleSearch = () => {
		const nextFilters = {
			status: filters.status,
			ticketState: filters.ticketState.trim(),
			accountId: filters.accountId.trim(),
			conversationId: filters.conversationId.trim(),
			ticketId: filters.ticketId.trim(),
		};
		startingAfterByPage.current = {};
		setAppliedFilters(nextFilters);
		setPage(1);
		setSearchParams({
			...nextFilters,
			page: '1',
		});
	};

	const handleReset = () => {
		const cleared = { status: 'all', ticketState: '', accountId: '', conversationId: '', ticketId: '' };
		startingAfterByPage.current = {};
		setFilters(cleared);
		setAppliedFilters(cleared);
		setPage(1);
		setSearchParams({
			...cleared,
			page: '1',
		});
	};

	const columns = useMemo(
		() => [
			{
				header: 'View',
				id: 'view',
				Cell: ({ row }) => {
					const conversationId = getConversationId(row.original);
					const ticketDetailId = getTicketDetailId(row.original);
					const hasConversation = Boolean(conversationId);
					return (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							{hasConversation ? (
								<Tooltip title="View conversation + ticket details">
									<span>
										<Button
											variant="text"
											size="small"
											onClick={() => navigate(`/intercom/conversations/${conversationId}`)}
										>
											<ChatBubbleOutlineIcon fontSize="small" />
											<ConfirmationNumberIcon fontSize="small" />
										</Button>
									</span>
								</Tooltip>
							) : (
								<Tooltip title="Click to see the ticket details">
									<span>
										<Button
											variant="text"
											size="small"
											disabled={!ticketDetailId}
											onClick={() => navigate(`/intercom/tickets/${ticketDetailId}`)}
										>
											<ConfirmationNumberIcon fontSize="small" />
										</Button>
									</span>
								</Tooltip>
							)}
						</Box>
					);
				},
			},
			{
				header: 'Author Email',
				accessorFn: (row) => getAuthorEmail(row),
				id: 'authorEmail',
			},

			{
				header: 'Open',
				accessorFn: (row) => (row?.open ? 'Open' : 'Closed'),
				id: 'open',
			},
			{
				header: 'State',
				accessorFn: (row) => row?.ticket_state?.external_label || row?.ticket_state?.category || '',
				id: 'state',
			},
			{
				header: 'Title',
				accessorFn: (row) => row?.ticket_attributes?._default_title_ || '',
				id: 'title',
			},

			{
				header: 'Account ID',
				accessorFn: (row) => getTicketAccountId(row),
				id: 'accountId',
			},
			{
				header: 'Created',
				accessorFn: (row) => formatTimestamp(row?.created_at),
				id: 'createdAt',
			},
			{
				header: 'Updated',
				accessorFn: (row) => formatTimestamp(row?.updated_at),
				id: 'updatedAt',
			},
			{
				header: 'Conversation ID',
				accessorFn: (row) => getConversationId(row) || '',
				id: 'conversationId',
			},
			{
				header: 'Ticket ID',
				accessorFn: (row) => row?.ticket_id || row?.id || '',
				id: 'ticketId',
			},
			{
				header: 'Channel',
				accessorFn: (row) => row?.channel || '',
				id: 'channel',
			},
		],
		[navigate]
	);

	return (
		<div className={styles.divStyle}>
			<div className={styles.sectionContainer}>
				<div className={styles.sectionHeader}>
					<Tooltip title="Intercom">
						<button
							type="button"
							className={styles.backButton}
							onClick={() => navigate('/intercom')}
						>
							<ArrowBackIcon className={styles.backIcon} />
						</button>
					</Tooltip>
					<div>
						<h1 className={styles.sectionTitle}>Intercom Tickets</h1>
						<p className={styles.sectionDescription}>
							Search tickets by status, state, account ID, or ticket ID.
						</p>
					</div>
				</div>
			</div>

			<Paper className={styles.tableContainer}>
				<Box className={styles.filtersRow}>
					<FormControl size="small" className={styles.filterField}>
						<InputLabel>Status</InputLabel>
						<Select
							label="Status"
							value={filters.status}
							onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
						>
							{STATUS_OPTIONS.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<FormControl size="small" className={styles.filterField}>
						<InputLabel>Ticket State</InputLabel>
						<Select
							label="Ticket State"
							value={filters.ticketState}
							onChange={(event) => setFilters((prev) => ({ ...prev, ticketState: event.target.value }))}
						>
							{TICKET_STATE_OPTIONS.map((option) => (
								<MenuItem key={option.value || 'all'} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<TextField
						label="Account ID"
						variant="outlined"
						size="small"
						value={filters.accountId}
						onChange={(event) => setFilters((prev) => ({ ...prev, accountId: event.target.value }))}
						className={styles.filterField}
					/>
					<TextField
						label="Conversation ID"
						variant="outlined"
						size="small"
						value={filters.conversationId}
						onChange={(event) => setFilters((prev) => ({ ...prev, conversationId: event.target.value }))}
						className={styles.filterField}
					/>
					<TextField
						label="Ticket ID"
						variant="outlined"
						size="small"
						value={filters.ticketId}
						onChange={(event) => setFilters((prev) => ({ ...prev, ticketId: event.target.value }))}
						className={styles.filterField}
					/>
					<Box className={styles.filterActions}>
						<Button variant="contained" onClick={handleSearch} disabled={isLoading}>
							Search
						</Button>
						<Button variant="outlined" onClick={handleReset} disabled={isLoading}>
							Reset
						</Button>
					</Box>
				</Box>

				<Box className={styles.metaRow}>
					<Typography variant="body2">
						{totalCount ? `Total tickets: ${totalCount}` : 'No tickets found.'}
					</Typography>
					{errorMessage && (
						<Typography variant="body2" color="error">
							{errorMessage}
						</Typography>
					)}
				</Box>

				<MaterialReactTable
					columns={columns}
					data={isLoading ? [] : tickets}
					enableColumnOrdering
					enableGlobalFilter
					enableColumnFilters={false}
					enableRowSelection={false}
					enableStickyHeader
					manualPagination
					rowCount={totalCount}
					enablePagination
					muiTablePaginationProps={{
						rowsPerPageOptions: [10],
					}}
					onPaginationChange={(updater) => {
						const next = typeof updater === 'function'
							? updater({ pageIndex: page - 1, pageSize: DEFAULT_PAGE_SIZE })
							: updater;
						const nextPage = next.pageIndex + 1;
						setPage(nextPage);
						setSearchParams({
							...appliedFilters,
							page: String(nextPage),
						});
					}}
					state={{
						isLoading,
						pagination: { pageIndex: page - 1, pageSize: DEFAULT_PAGE_SIZE },
					}}
					initialState={{ density: 'compact', pagination: { pageSize: DEFAULT_PAGE_SIZE } }}
					muiTablePaperProps={{ elevation: 0 }}
					muiTableContainerProps={{ className: styles.tableWrapper }}
				/>
			</Paper>
		</div>
	);
};

export default IntercomTickets;
